if (!customElements.get("search-category-select")) {
  class SearchCategorySelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector(".search-category-select__btn");
      this.listbox = this.querySelector(".search-category-select__listbox");
      this.options = this.querySelectorAll(".search-category-select__option");
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.buttonText = this.button.querySelector("span");
      this.selectedOption = null;
      this.focusedOption = null;
      this.isOpen = false;
      this.searchString = "";
      this.searchTimer = null;

      this.init();
    }

    init() {
      // Check current page URL and find matching option
      const currentPath = window.location.pathname;
      let matchedOption = null;

      // Find option that matches current URL
      this.options.forEach((option) => {
        const optionValue = option.dataset.value;
        if (optionValue && currentPath.includes(optionValue)) {
          matchedOption = option;
        }
      });

      // Set initial selected option
      if (matchedOption) {
        // If current page matches a category, select it
        this.selectedOption = matchedOption;
        this.selectedOption.setAttribute("aria-selected", "true");
        this.buttonText.textContent = matchedOption.textContent.trim();

        // Update native select
        if (this.nativeSelect) {
          this.nativeSelect.value = matchedOption.dataset.value;
        }

        // Remove aria-selected from placeholder
        this.options[0].setAttribute("aria-selected", "false");
      } else {
        // Default to placeholder
        this.selectedOption = this.options[0];
        this.selectedOption.setAttribute("aria-selected", "true");
      }

      // Add event listeners
      this.button.addEventListener("click", this.toggleListbox.bind(this));
      this.button.addEventListener(
        "keydown",
        this.handleButtonKeydown.bind(this)
      );

      // Close on click outside
      document.addEventListener("click", (e) => {
        if (!this.contains(e.target) && this.isOpen) {
          this.closeListbox();
        }
      });

      // Option click handlers
      this.options.forEach((option) => {
        option.addEventListener("click", () => {
          this.selectOption(option);
        });

        option.addEventListener("mouseenter", () => {
          this.focusOption(option);
        });
      });

      // Listbox keyboard navigation
      this.listbox.addEventListener(
        "keydown",
        this.handleListboxKeydown.bind(this)
      );
    }

    toggleListbox() {
      if (this.isOpen) {
        this.closeListbox();
      } else {
        this.openListbox();
      }
    }

    openListbox() {
      this.isOpen = true;
      this.classList.add("is-open");
      this.button.setAttribute("aria-expanded", "true");
      this.listbox.hidden = false;
      this.listbox.setAttribute("aria-hidden", "false");

      // Focus the selected option or first option
      const optionToFocus = this.selectedOption || this.options[0];
      this.focusOption(optionToFocus);
      this.listbox.focus();
    }

    closeListbox() {
      this.isOpen = false;
      this.classList.remove("is-open");
      this.button.setAttribute("aria-expanded", "false");
      this.listbox.hidden = true;
      this.listbox.setAttribute("aria-hidden", "true");

      if (this.focusedOption) {
        this.focusedOption.classList.remove("is-focused");
      }

      this.button.focus();
    }

    selectOption(option) {
      // Don't allow selecting disabled options
      if (option.classList.contains("is-disabled")) {
        return;
      }

      // Remove aria-selected from previously selected option
      if (this.selectedOption) {
        this.selectedOption.setAttribute("aria-selected", "false");
      }

      // Set new selected option
      this.selectedOption = option;
      option.setAttribute("aria-selected", "true");

      // Update button text with selected option text
      const optionText = option.textContent.trim();
      this.buttonText.textContent = optionText;

      // Update native select value
      const optionValue = option.dataset.value;
      if (this.nativeSelect) {
        this.nativeSelect.value = optionValue;
        this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Update aria-activedescendant
      this.listbox.setAttribute("aria-activedescendant", option.id);

      // Dispatch custom change event
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: {
            value: optionValue,
            text: optionText,
          },
        })
      );

      // Redirect to category URL if value is not empty
      if (optionValue && optionValue !== "") {
        window.location.href = optionValue;
        return; // Don't close listbox since we're redirecting
      }

      // Close the listbox
      this.closeListbox();
    }

    focusOption(option) {
      // Remove focus from currently focused option
      if (this.focusedOption) {
        this.focusedOption.classList.remove("is-focused");
      }

      // Set focus on new option
      this.focusedOption = option;
      option.classList.add("is-focused");

      // Scroll option into view if needed
      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop;
        const optionBottom = option.offsetTop + option.offsetHeight;

        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight;
        } else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }

    handleButtonKeydown(e) {
      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp":
        case " ":
        case "Enter":
          e.preventDefault();
          this.openListbox();
          break;
      }
    }

    handleListboxKeydown(e) {
      let nextOption;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          nextOption = this.getNextOption(this.focusedOption);
          if (nextOption) {
            this.focusOption(nextOption);
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          nextOption = this.getPreviousOption(this.focusedOption);
          if (nextOption) {
            this.focusOption(nextOption);
          }
          break;

        case "Enter":
        case " ":
          e.preventDefault();
          if (this.focusedOption) {
            this.selectOption(this.focusedOption);
          }
          break;

        case "Escape":
          e.preventDefault();
          this.closeListbox();
          break;

        case "Tab":
          e.preventDefault();
          this.closeListbox();
          break;

        case "Home":
          e.preventDefault();
          this.focusOption(this.options[0]);
          break;

        case "End":
          e.preventDefault();
          this.focusOption(this.options[this.options.length - 1]);
          break;

        default:
          // Type-ahead search
          if (e.key.length === 1) {
            this.searchByTyping(e.key);
          }
          break;
      }
    }

    getNextOption(currentOption) {
      const currentIndex = Array.from(this.options).indexOf(currentOption);
      for (let i = currentIndex + 1; i < this.options.length; i++) {
        if (!this.options[i].classList.contains("is-disabled")) {
          return this.options[i];
        }
      }
      return null;
    }

    getPreviousOption(currentOption) {
      const currentIndex = Array.from(this.options).indexOf(currentOption);
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (!this.options[i].classList.contains("is-disabled")) {
          return this.options[i];
        }
      }
      return null;
    }

    searchByTyping(key) {
      this.searchString += key.toLowerCase();

      // Clear existing timer
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // Find matching option
      const matchingOption = Array.from(this.options).find((option) => {
        const text = option.textContent.trim().toLowerCase();
        return (
          text.startsWith(this.searchString) &&
          !option.classList.contains("is-disabled")
        );
      });

      if (matchingOption) {
        this.focusOption(matchingOption);
      }

      // Reset search string after 500ms
      this.searchTimer = setTimeout(() => {
        this.searchString = "";
      }, 500);
    }
  }

  customElements.define("search-category-select", SearchCategorySelect);
}
